import {
  Group,
  Mesh,
  MeshLambertMaterial,
  PerspectiveCamera,
  PointLight,
  Scene,
  SphereGeometry,
  WebGLRenderer,
  Vector3,
} from "three";

import { ResourcesOptions, commonOption, XYZ } from "./resources";
import TWEEN from "@tweenjs/tween.js";
import SuperGround from "./superGround";
import { AllRes } from "./utils";

class SuperGlobe {
  mode: number = 0;
  renderer: WebGLRenderer;
  animationFrameId: number = 0;
  camera: PerspectiveCamera;
  sunLight: PointLight;
  sunOrbit: Group;
  globe: Group;
  globeBackground: Mesh;
  globeSphere: Mesh;
  scene: Scene;
  lastSunOrbitRotate: number = 0;

  onTweening: boolean = false;

  constructor(
    canvasElement: HTMLCanvasElement,
    public res: ResourcesOptions,
    public groundInstance: SuperGround,
    public loaderManager: AllRes
  ) {
    // 资源和配置
    this.res = res;

    // 初始化渲染器
    this.renderer = new WebGLRenderer({
      alpha: true,
      antialias: true,
      canvas: canvasElement,
    });

    // 添加各个部件
    this.camera = new PerspectiveCamera(); // 视角
    this.sunOrbit = new Group(); // 太阳轨道
    this.sunLight = new PointLight("white"); // 太阳
    this.globe = new Group(); // 地球
    this.globeBackground = new Mesh(); // 星空背景（黑色、星星）
    this.globeSphere = new Mesh(); // 球体
    this.scene = new Scene();

    this.sunOrbit.add(this.sunLight);
    this.scene.add(this.sunOrbit);
    this.globe.add(this.globeBackground);
    this.globe.add(this.globeSphere);
    this.scene.add(this.camera);
    this.scene.add(this.globe);

    // 具体设置每个部件
    this.setGlobe();
    this.setSunLight();
    this.setCamera();

    // 更新大小
    this.updateSize();
  }
  // 循环器
  animate(time?): void {
    this.render(time);
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
  }
  // 渲染部分
  render(time): void {
    this.renderer.sortObjects = false;
    this.renderer.render(this.scene, this.camera);

    // 更新星球的自转
    this.updateGlobeRotate();
    // 更新太阳的轨道
    this.updateSunLightPos();

    TWEEN.update(time);
  }
  updateGlobeRotate() {
    this.globeSphere.rotateY(
      this.onTweening
        ? this.res.globeTweenRotateSpeed
        : this.res.modes[this.mode].globeRotateSpeed
    );
  }
  updateSunLightPos() {
    if (!this.onTweening && this.mode === 0) {
      // 让轨道直接绕 x 轴旋转
      this.sunOrbit.rotateX(Math.PI / 360 / 12);
    }
  }

  // 设置星球
  setGlobe() {
    const map = this.loaderManager.globeTexture;
    // 设置形状和顶点数
    this.globeSphere.geometry = new SphereGeometry(
      commonOption.RADIUS,
      commonOption.GLOBE_SEGMENTS,
      commonOption.GLOBE_SEGMENTS
    );
    // 添加材质
    this.globeSphere.material = new MeshLambertMaterial({
      map,
    });
  }
  // 设置太阳
  setSunLight() {
    // 初始化轨道位置（仅控制旋转）
    this.sunOrbit.position.setX(this.res.sunLightOrbitPos);
    // 相对轨道的位置
    this.sunLight.position.copy(this.res.sunLightPosition);
    // 设置强度
    this.sunLight.intensity = this.res.sunLightIntensity;
  }
  // 视角设置
  setCamera() {
    // 设置默认值
    const mode = this.res.modes[this.mode];
    const cameraPos = { ...mode.cameraPosition };
    const cameraLookAt = { ...mode.cameraLookAt };

    this.camera.position.set(cameraPos.x, cameraPos.y, cameraPos.z);
    this.camera.lookAt(cameraLookAt.x, cameraLookAt.y, cameraLookAt.z);

    this.camera.far = commonOption.CAMERA_FAR;
    this.camera.fov = commonOption.CAMERA_FOV;
    this.camera.near = commonOption.CAMERA_NEAR;
  }
  // 更新视角大小
  updateSize(): void {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
  nextMode() {
    const beforeMode = this.mode;
    if (this.mode === 0) {
      // 第二种视角（轨道视角）
      this.mode = 1;
    } else if (this.mode === 1) {
      // 第一种视角（鸟瞰全貌）
      this.mode = 2;
      this.groundInstance.zoomIn();
    } else if (this.mode === 2) {
      // 第一种视角（鸟瞰全貌）
      this.mode = 0;
      this.groundInstance.zoomOut();
    }
    const modeBefore = this.res.modes[beforeMode];
    const modeNow = this.res.modes[this.mode];
    const duration = this.res.tweenDuration;

    // tween 初始值和更新事件
    const sunOrbitRotate = this.sunOrbit.rotation.x;
    const sunOrbitTween = new TWEEN.Tween({ x: sunOrbitRotate });
    sunOrbitTween.onUpdate((o: {x: number}) => {
      this.sunOrbit.setRotationFromAxisAngle(new Vector3(1, 0, 0), o.x);
    });

    if (this.mode === 1) {
      // 从随机角度到固定角度
      this.lastSunOrbitRotate = sunOrbitRotate;
      sunOrbitTween.to({ x: 0 }, duration).start();
    } else if (this.mode === 0) {
      // 从固定角度到上次角度
      sunOrbitTween.to({ x: this.lastSunOrbitRotate }, duration).start();
    }

    const cameraPosAtTween = new TWEEN.Tween({ ...modeBefore.cameraPosition });
    const cameraLookAtTween = new TWEEN.Tween({ ...modeBefore.cameraLookAt });
    cameraPosAtTween
      .easing(TWEEN.Easing.Cubic.InOut)
      .onUpdate((o: XYZ) => {
        this.camera.position.set(o.x, o.y, o.z);
      })
      .onStart(() => {
        this.onTweening = true;
      })
      .onComplete(() => {
        this.onTweening = false;
      })
      .onStop(() => {
        this.onTweening = false;
      });
    cameraLookAtTween.easing(TWEEN.Easing.Cubic.InOut).onUpdate((o: XYZ) => {
      this.camera.lookAt(o.x, o.y, o.z);
    });

    cameraPosAtTween.to({ ...modeNow.cameraPosition }, duration).start();
    cameraLookAtTween.to({ ...modeNow.cameraLookAt }, duration).start();

    return this.mode;
  }
}
export default SuperGlobe;
